'use client';

import Link from 'next/link';
import { ArrowUpRight, Clock, Mail, MapPin, Phone, type LucideIcon } from 'lucide-react';

import { trackContactLinkClick } from '@/lib/analytics/contact-link-tracking';
import { trackCtaClick } from '@/lib/analytics/track-cta-click';
import { Button } from '@/components/ui/button';
import { legalNavHref } from '@/components/navbar/legal-nav-href';
import { DrawerClose, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { cn } from '@/lib/utils';
import type { CompanyContactRow } from '@/sanity/lib/company-contact-rows';
import type { SanityNavLink } from '@/sanity/lib/navigation';

export const drawerSectionHeadingClass =
  'font-sans text-[10px] font-semibold uppercase tracking-widest text-muted-foreground';

const contactIcons = {
  mail: Mail,
  phone: Phone,
  mapPin: MapPin,
  clock: Clock,
} satisfies Record<string, LucideIcon>;

export type NavbarMobileMenuLinkResolution = 'sticky' | 'legal';

export type NavbarMobileMenuContentProps = {
  direction: 'bottom' | 'top' | 'left' | 'right';
  showHomeLink?: boolean;
  linkResolution?: NavbarMobileMenuLinkResolution;
  isHome?: boolean;
  links: SanityNavLink[];
  mobileContactRows: CompanyContactRow[];
  mobileCta: SanityNavLink | null | undefined;
};

function menuHref(
  linkResolution: NavbarMobileMenuLinkResolution,
  isHome: boolean,
  href: string,
): string {
  if (linkResolution === 'sticky') return href;
  return isHome ? href : legalNavHref(href);
}

export function NavbarMobileMenuContent({
  direction,
  showHomeLink = false,
  linkResolution = 'sticky',
  isHome = true,
  links,
  mobileContactRows,
  mobileCta,
}: NavbarMobileMenuContentProps) {
  return (
    <DrawerContent
      className={cn(
        'p-0 dark:bg-neutral-950 space-y-3 md:rounded-none!',
        direction === 'bottom' && 'max-h-[min(88vh,640px)]',
      )}
      showHandle={direction === 'bottom'}
    >
      <DrawerHeader className="max-sm:sr-only sm:py-0 text-left! sm:px-7 md:pt-6 md:pb-3">
        <DrawerTitle className="max-sm:sr-only text-lg md:text-2xl font-normal tracking-wide text-left!">
          Site Navigation Menu
        </DrawerTitle>
      </DrawerHeader>
      <div className="grid sm:grid-cols-2 md:grid-cols-1 gap-3 px-4 sm:px-6">
        {showHomeLink ? (
          <DrawerClose asChild>
            <Link
              href="/"
              className="px-4 text-sm font-normal uppercase tracking-widest text-foreground bg-neutral-50 border min-h-11 h-12 inline-flex items-center text-left justify-between rounded-lg"
              onClick={() =>
                trackCtaClick({ location: 'mobile_menu_link', label: 'Home', href: '/' })
              }
            >
              Home
              <ArrowUpRight className="size-4 shrink-0 text-muted-foreground" />
            </Link>
          </DrawerClose>
        ) : null}
        {links.map((link) => (
          <DrawerClose key={`${link.href}-${link.label}`} asChild>
            <Link
              href={menuHref(linkResolution, isHome, link.href)}
              className="px-4 text-sm font-normal uppercase tracking-widest text-foreground bg-neutral-50 border min-h-11 h-12 inline-flex items-center text-left justify-between rounded-lg"
              onClick={() =>
                trackCtaClick({
                  location: 'mobile_menu_link',
                  label: link.label,
                  href: menuHref(linkResolution, isHome, link.href),
                })
              }
            >
              {link.label}
              <ArrowUpRight className="size-4 shrink-0 text-muted-foreground" />
            </Link>
          </DrawerClose>
        ))}
      </div>
      {mobileContactRows.length > 0 ? (
        <ul className="flex flex-col gap-3 px-4 sm:px-6">
          {mobileContactRows.map((row) => {
            const Icon = contactIcons[row.iconKey];
            const body = (
              <span className="flex items-center gap-3 text-sm text-foreground">
                <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                <span className="wrap-break-word whitespace-pre-line font-normal leading-snug">
                  {row.value}
                </span>
              </span>
            );
            return (
              <DrawerClose key={row.value} asChild>
                <Link
                  href={row.href ?? '/'}
                  onClick={() => {
                    if (row.href) trackContactLinkClick(row.href, 'mobile_menu');
                  }}
                  className="px-4 text-sm font-normal uppercase tracking-widest text-foreground bg-neutral-50 border min-h-11 h-12 inline-flex items-center text-left justify-between rounded-lg"
                >
                  {body}
                  <ArrowUpRight className="size-4 shrink-0 text-muted-foreground" />
                </Link>
              </DrawerClose>
            );
          })}
        </ul>
      ) : null}
      {mobileCta ? (
        <DrawerClose asChild>
          <Link
            href={menuHref(linkResolution, isHome, mobileCta.href)}
            className="px-4 sm:px-6 pb-4"
            data-lead-form-trigger={mobileCta.isLeadForm ? 'true' : undefined}
            data-tracking-location="mobile_menu_cta"
            onClick={() =>
              trackCtaClick({
                location: 'mobile_menu_cta',
                label: mobileCta.label,
                href: menuHref(linkResolution, isHome, mobileCta.href),
                isLeadForm: mobileCta.isLeadForm,
                variant: 'primary',
              })
            }
          >
            <Button className="group h-11 px-4 w-full rounded-lg bg-[#948B7F] text-sm font-semibold uppercase tracking-widest text-white hover:bg-[#BFB7A1] hover:text-white inline-flex items-center justify-between">
              <span>{mobileCta.label}</span>
              <ArrowUpRight className="size-4 shrink-0 text-white" />
            </Button>
          </Link>
        </DrawerClose>
      ) : null}
    </DrawerContent>
  );
}
